import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";

import {
  IDashboardCampaignEffectiveness,
  IDashboardCampaignRoi,
} from "#interfaces/dashboard/campaign.interface";
import { CategorySalesMetric } from "#interfaces/dashboard/category-sales-metric.interface";
import { ClientsPerProject } from "#interfaces/dashboard/clients-per-project.interface";
import { ICommissionCenter } from "#interfaces/dashboard/commission-center.interface";
import { IDashboardInteriorTypeProduct } from "#interfaces/dashboard/interior-type-metric.interface";
import { LeadsKpiData } from "#interfaces/dashboard/leads-kpi.interface";
import { LostOpportunityMetric } from "#interfaces/dashboard/lost-opportunity-metric.interface";
import { NumSalesAdvisor } from "#interfaces/dashboard/num-sales-advisor.interface";
import { OpportunityAdvisor } from "#interfaces/dashboard/opportunity-advisor.interface";
import { OpportunityDeparmentMetric } from "#interfaces/dashboard/opportunity-deparment-metric.interface";
import {
  OpportunitiesKpiParams,
  OpportunityKpiMetric,
  PaymentRequestKPI,
  SalesPerMonthMetric,
  SalesPerMonthWeekMetric,
} from "#interfaces/dashboard/opportunity-kpi.interface";
import { OpportunityReasonMetric } from "#interfaces/dashboard/opportunity-reason-metric.interface";
import { OportunityResumeMetric } from "#interfaces/dashboard/opportunity-resume-metric.interface";
import { OpportunityWinLossReasonMetric } from "#interfaces/dashboard/opportunity-resume-reason-metric.interface";
import { OpportunityStepMetric } from "#interfaces/dashboard/opportunity-step-metric.interface";
import { PendingActivities } from "#interfaces/dashboard/pending-activities.interface";
import { ProducStatusData } from "#interfaces/dashboard/product-status-kpi.interface";
import { ProductYearSalesMetric } from "#interfaces/dashboard/product-year-sales-metric.interface";
import { BarChartMetric } from "#interfaces/dashboard/receipt-bank-metric.interface";
import { SalesByAdvisor } from "#interfaces/dashboard/sales-by-advisor.interface";
import { ITotalComparativeKpi } from "#interfaces/dashboard/total-comparative-kpi.interface";
import {
  ApiResponseData,
  BankTypeProductMetricResponse,
  CategoriesResponse,
  CategorySalesMetricResponse,
  GoalMetricsResponse,
  InteriorTypeProductMetricResponse,
  LostOpportunityMetricResponse,
  NumSalesAdvisorResponse,
  OpportunitiesResponse,
  OpportunityAdvisorResponse,
  OpportunityDeparmentMetricResponse,
  OpportunityReasonMetricsResponse,
  OpportunityResumeMetricResponse,
  OpportunityStepMetricResponse,
  PendingActivitiesResponse,
  ProductYearSalesMetricResponse,
  SalesByAdvisorResponse,
  WinLossReasonsMetricResponse,
} from "#interfaces/http-response/responses/http-responses";
import { Opportunity } from "#interfaces/opportunity/opportunity.interface";
import { cleanParams } from "#utils/clean-params/clean-params.helper";
import { HeadersHttpService } from "#utils/http-headers/http-headers.service";
import { environment } from "environments/environment";
import { BehaviorSubject, Observable, Subject, catchError, forkJoin, map, of, switchMap, tap, throwError } from "rxjs";
import { ProspectFunnel } from "#interfaces/dashboard/prospect-funnel.interface";
import { LeadFunnelKpi, LeadFunnelSource, LeadFunnelView } from "#interfaces/dashboard/lead-funnel-kpi.interface";
import {
  DocumentationComplianceKpi,
  PaymentSapReconciliationKpi,
  ReservationContractKpi,
  ReservationContractReport,
} from "#interfaces/dashboard/sales-administration-kpi.interface";
import { InternalExternalSalesKpi } from "#interfaces/dashboard/internal-external-sales-kpi.interface";
import { PenaltyIncomeKpi } from "#interfaces/dashboard/penalty-income-kpi.interface";
import { ProductsOverdueAmountKpi } from "#interfaces/dashboard/products-overdue-amount-kpi.interface";
import { LossReasonKpi } from "#interfaces/dashboard/loss-reason-kpi.interface";
import { LeadAdvisorKpi } from "#interfaces/dashboard/lead-advisor-kpi.interface";
import { AppreciationKpi } from "#interfaces/dashboard/appreciation-kpi.interface";
import { ALL_COMPANIES_KEY, InterCompany, InterCompanyProject, ReportScope } from "#interfaces/dashboard/inter-company.interface";

export interface DashboardDateRange {
  initDate: Date | null;
  endDate: Date | null;
}

@Injectable({
  providedIn: "root",
})
export class DashboardService {
  // Projects Selected
  private readonly _currentProject: BehaviorSubject<number> =
    new BehaviorSubject(null);
  private readonly _goalMetricProject: BehaviorSubject<GoalMetricsResponse | null> =
    new BehaviorSubject(null);
  public readonly storageKey = "TotalComparativeKpi";
  public readonly cacheDuration = 10 * 60 * 1000; // 10 minutes in milliseconds

  //Opportunity
  private readonly _opportunityStepMetric: BehaviorSubject<
    OpportunityStepMetric[] | null
  > = new BehaviorSubject(null);
  private readonly _opportunityFunnelStepMetric: BehaviorSubject<
    OpportunityStepMetric[] | null
  > = new BehaviorSubject(null);
  private readonly _opportunityLossReasonMetric: BehaviorSubject<
    OpportunityReasonMetric[] | null
  > = new BehaviorSubject(null);
  private readonly _opportunityWinReasonMetric: BehaviorSubject<
    OpportunityReasonMetric[] | null
  > = new BehaviorSubject(null);
  private readonly _topOpportunitiesMetric: BehaviorSubject<
    Opportunity[] | null
  > = new BehaviorSubject(null);
  private readonly _lostOpportunityMetric: BehaviorSubject<
    LostOpportunityMetric[] | null
  > = new BehaviorSubject(null);
  private readonly _opportunityOriginMetric: BehaviorSubject<
    OpportunityReasonMetric[] | null
  > = new BehaviorSubject(null);
  private readonly _opportunityResumeMetric: BehaviorSubject<
    OportunityResumeMetric[] | null
  > = new BehaviorSubject(null);
  private readonly _opportunityDeparmentMetric: BehaviorSubject<
    OpportunityDeparmentMetric[] | null
  > = new BehaviorSubject(null);
  private readonly _opportunityWinLossReasonResumeMetric: BehaviorSubject<
    OpportunityWinLossReasonMetric[] | null
  > = new BehaviorSubject(null);
  private readonly _salesPerMonthMetric: BehaviorSubject<ApiResponseData<{
    salesPerMonth: SalesPerMonthMetric[];
    salesPerWeek: SalesPerMonthWeekMetric[];
  }> | null> = new BehaviorSubject(null);
  private readonly _paymentRequestKPI: BehaviorSubject<ApiResponseData<PaymentRequestKPI> | null> =
    new BehaviorSubject(null);
  private readonly _clientsPerProject: BehaviorSubject<ApiResponseData<
    ClientsPerProject[]
  > | null> = new BehaviorSubject(null);
  private readonly _commissionCenter: BehaviorSubject<ApiResponseData<ICommissionCenter> | null> =
    new BehaviorSubject(null);

  //Advisors
  private readonly _pendingActivitiesMetric: BehaviorSubject<
    PendingActivities[] | null
  > = new BehaviorSubject(null);
  private readonly _salesByAdvisor: BehaviorSubject<SalesByAdvisor[] | null> =
    new BehaviorSubject(null);
  private readonly _numSalesByAdvisor: BehaviorSubject<
    NumSalesAdvisor[] | null
  > = new BehaviorSubject(null);
  private readonly _opportunityAdvisor: BehaviorSubject<
    OpportunityAdvisor[] | null
  > = new BehaviorSubject(null);

  //Products
  private readonly _productYearSalesMetric: BehaviorSubject<
    ProductYearSalesMetric[] | null
  > = new BehaviorSubject(null);
  private readonly _categorySalesMetric: BehaviorSubject<
    CategorySalesMetric[] | null
  > = new BehaviorSubject(null);
  private readonly _categoryNumProductSoldMetric: BehaviorSubject<
    CategorySalesMetric[] | null
  > = new BehaviorSubject(null);
  private readonly _interiorTypeProductMetric: BehaviorSubject<IDashboardInteriorTypeProduct | null> =
    new BehaviorSubject(null);
  private readonly _paymentTypeMetric: BehaviorSubject<IDashboardInteriorTypeProduct | null> =
    new BehaviorSubject(null);
  private readonly _bankTypeMetric: BehaviorSubject<BarChartMetric | null> =
    new BehaviorSubject(null);

  // Meta Stats
  private readonly _campaignRoiMetric: BehaviorSubject<IDashboardCampaignRoi | null> =
    new BehaviorSubject(null);
  private readonly _campaignEffectivenessMetric: BehaviorSubject<IDashboardCampaignEffectiveness | null> =
    new BehaviorSubject(null);

  // KPIS

  private readonly _opportunitiesKpiMetrics: BehaviorSubject<
    ApiResponseData<OpportunityKpiMetric>
  > | null = new BehaviorSubject(null);

  private readonly _productStatusKpi: BehaviorSubject<
    ApiResponseData<ProducStatusData | null>
  > = new BehaviorSubject(null);

  private readonly _leadsKpi: BehaviorSubject<
    ApiResponseData<LeadsKpiData | null>
  > = new BehaviorSubject(null);

  private readonly _totalComparativeKpi: BehaviorSubject<
    ApiResponseData<ITotalComparativeKpi | null>
  > = new BehaviorSubject(null);

  private readonly _prospectFunnelKpi: BehaviorSubject<
    ApiResponseData<ProspectFunnel[] | null>
  > = new BehaviorSubject(null);

  // Global date range set by the dashboard filter — read by child KPI components
  public currentInitDate: string | null = null;
  public currentEndDate: string | null = null;
  private readonly _dateRange: BehaviorSubject<DashboardDateRange> = new BehaviorSubject<DashboardDateRange>({
    initDate: null,
    endDate: null,
  });
  public currentFunnelAdvisorId: string | null = null;

  private readonly baseUrl: string = environment.baseUrl + "Dashboard/";

  constructor(
    private readonly httpClient: HttpClient,
    private readonly httpHeaders: HeadersHttpService,
  ) {}

  // getters
  get clientsPerProject$(): Observable<ApiResponseData<ClientsPerProject[]>> {
    return this._clientsPerProject.asObservable();
  }

  get currenProjectId() {
    const advisorProjectId = Buffer.from(
      localStorage.getItem("idProject") ?? "",
      "base64",
    ).toString();
    const projectIdPersistId = Number(
      localStorage.getItem("selectedProjectId"),
    );
    const adminProject = projectIdPersistId;

    const isAdmin =
      localStorage.getItem("rol") === "SuperAdmin" ||
      localStorage.getItem("rol") === "Manager" ||
      localStorage.getItem("rol") === "SalesAdvisor" ||
      localStorage.getItem("rol") === "CommercialSpecialist" ||
      localStorage.getItem("rol") === "OnlyViewer" ||
      localStorage.getItem("rol") === "Admin" ||
      localStorage.getItem("rol") === "CreditManager" ||
      localStorage.getItem("rol") === "FinancialAnalyst" ||
      localStorage.getItem("rol") === "CommunityManager";
    if (isAdmin) {
      localStorage.setItem("selectedProjectId", adminProject.toString());
      return adminProject;
    }

    return Number(advisorProjectId.split(",")[0]);
  }

  get campaignEffectivenessMetric$(): Observable<IDashboardCampaignEffectiveness> {
    return this._campaignEffectivenessMetric.asObservable();
  }

  get campaignRoiMetric$(): Observable<IDashboardCampaignRoi> {
    return this._campaignRoiMetric.asObservable();
  }

  get opportunityDeparmentMetric$(): Observable<OpportunityDeparmentMetric[]> {
    return this._opportunityDeparmentMetric.asObservable();
  }

  get goaltProject$(): Observable<GoalMetricsResponse> {
    return this._goalMetricProject.asObservable();
  }

  get currentProject$(): Observable<number> {
    return this._currentProject.asObservable();
  }

  get dateRange$(): Observable<DashboardDateRange> {
    return this._dateRange.asObservable();
  }

  get currentDateRange(): DashboardDateRange {
    return this._dateRange.getValue();
  }

  private readonly _dateRangeRequest: Subject<DashboardDateRange> = new Subject<DashboardDateRange>();

  get dateRangeRequest$(): Observable<DashboardDateRange> {
    return this._dateRangeRequest.asObservable();
  }

  RequestDateRange(initDate: Date | null, endDate: Date | null) {
    this._dateRangeRequest.next({ initDate, endDate });
  }

  SetDateRange(initDate: Date | string | null, endDate: Date | string | null) {
    this._dateRange.next({
      initDate: initDate ? new Date(initDate) : null,
      endDate: endDate ? new Date(endDate) : null,
    });
  }

  get opportunityAdvisor$(): Observable<OpportunityAdvisor[]> {
    return this._opportunityAdvisor.asObservable();
  }

  get salesByAdvisor$(): Observable<SalesByAdvisor[]> {
    return this._salesByAdvisor.asObservable();
  }

  get numSalesByAdvisor$(): Observable<NumSalesAdvisor[]> {
    return this._numSalesByAdvisor.asObservable();
  }

  get opportunityStepMetric$(): Observable<OpportunityStepMetric[]> {
    return this._opportunityStepMetric.asObservable();
  }

  get opportunityFunnelStepMetric$(): Observable<OpportunityStepMetric[]> {
    return this._opportunityFunnelStepMetric.asObservable();
  }

  get opportunityLossReasonMetric$(): Observable<OpportunityReasonMetric[]> {
    return this._opportunityLossReasonMetric.asObservable();
  }

  get opportunityWinReasonMetric$(): Observable<OpportunityReasonMetric[]> {
    return this._opportunityWinReasonMetric.asObservable();
  }

  get topOpportunitiesMetric$(): Observable<Opportunity[]> {
    return this._topOpportunitiesMetric.asObservable();
  }

  get lostOpportunityMetric$(): Observable<LostOpportunityMetric[]> {
    return this._lostOpportunityMetric.asObservable();
  }

  get opportunityOriginMetric$(): Observable<OpportunityReasonMetric[]> {
    return this._opportunityOriginMetric.asObservable();
  }

  get opportunityResumeMetric$(): Observable<OportunityResumeMetric[]> {
    return this._opportunityResumeMetric.asObservable();
  }

  get pendingActivitiesMetric$(): Observable<PendingActivities[]> {
    return this._pendingActivitiesMetric.asObservable();
  }

  get productYearSalesMetric$(): Observable<ProductYearSalesMetric[]> {
    return this._productYearSalesMetric.asObservable();
  }

  get categorySalesMetric$(): Observable<CategorySalesMetric[]> {
    return this._categorySalesMetric.asObservable();
  }

  get categoryNumProductSoldMetric$(): Observable<CategorySalesMetric[]> {
    return this._categoryNumProductSoldMetric.asObservable();
  }

  get winLossReasonResumeMetric$(): Observable<
    OpportunityWinLossReasonMetric[]
  > {
    return this._opportunityWinLossReasonResumeMetric.asObservable();
  }

  get interiorTypeProductMetric$(): Observable<IDashboardInteriorTypeProduct> {
    return this._interiorTypeProductMetric.asObservable();
  }

  get paymentType$(): Observable<IDashboardInteriorTypeProduct> {
    return this._paymentTypeMetric.asObservable();
  }

  get bankType$(): Observable<BarChartMetric> {
    return this._bankTypeMetric.asObservable();
  }

  get opportunitiesKpiMetrics$(): Observable<
    ApiResponseData<OpportunityKpiMetric>
  > {
    return this._opportunitiesKpiMetrics.asObservable();
  }

  get productStatusKpi$(): Observable<ApiResponseData<ProducStatusData>> {
    return this._productStatusKpi;
  }

  get leadsKpi$(): Observable<ApiResponseData<LeadsKpiData>> {
    return this._leadsKpi.asObservable();
  }

  get paymentRequestKpi$(): Observable<ApiResponseData<PaymentRequestKPI>> {
    return this._paymentRequestKPI.asObservable();
  }

  get totalComparativeKpi$(): Observable<
    ApiResponseData<ITotalComparativeKpi>
  > {
    return this._totalComparativeKpi.asObservable();
  }

  get salesPerMonthMetric$(): Observable<
    ApiResponseData<{
      salesPerMonth: SalesPerMonthMetric[];
      salesPerWeek: SalesPerMonthWeekMetric[];
    }>
  > {
    return this._salesPerMonthMetric.asObservable();
  }

  get commissionCenter$(): Observable<ApiResponseData<ICommissionCenter>> {
    return this._commissionCenter.asObservable();
  }

  get prospectFunnelKpi$(): Observable<ApiResponseData<ProspectFunnel[]>> {
    return this._prospectFunnelKpi.asObservable();
  }

  SetCurrentProject(id: number) {
    this._currentProject.next(id);
    localStorage.setItem("selectedProjectId", id.toString());
    this._currentProject.next(null);
  }

paymentRequestKPI(
  payload: {
    projectId?: number | null;
    initDate?: string | null;
    endDate?: string | null;
    allProjects?: boolean;
  } = { projectId: null, initDate: null, endDate: null },
): Observable<ApiResponseData<PaymentRequestKPI>> {
  const { projectId = null, initDate = null, endDate = null, allProjects = false } = payload;

  const params = cleanParams({ projectId: projectId, initDate, endDate, allProjects: allProjects || null });

  return this.httpClient
    .get<ApiResponseData<PaymentRequestKPI>>(
      this.baseUrl + "PaymentRequestKPI",
      {
        headers: this.httpHeaders.getHeadersPrivate(),
        params,
      },
    )
    .pipe(
      tap((res) => {
        this._paymentRequestKPI.next({
          ...res,
          data: {
            ...res.data,
            resolutionTime: res.data.resolutionTime
              .split(":")
              .map((time) =>
                parseInt(time)
                  .toLocaleString("es-HN", { maximumFractionDigits: 0 })
                  .padStart(2, "0"),
              )
              .join(":"),
          },
        });
      }),
      catchError((err) => {
        this._paymentRequestKPI.next({
          succeeded: false,
          message: err?.error?.Message ?? err?.error?.message ?? 'Error al obtener los datos.',
          errors: null,
          data: null
        });
        return throwError(() => err);
      }),
    );
}

  receiptBankMetrics(year: number): Observable<BankTypeProductMetricResponse> {
    return this.httpClient
      .get<BankTypeProductMetricResponse>(this.baseUrl + "ReceiptBankMetrics", {
        headers: this.httpHeaders.getHeadersPrivate(),
        params: { year },
      })
      .pipe(
        tap((res) => {
          this._bankTypeMetric.next(res.data);
        }),
        catchError((err) => {
          return throwError(() => err);
        }),
      );
  }

  paymentTypeMetrics(
    year: number,
  ): Observable<InteriorTypeProductMetricResponse> {
    return this.httpClient
      .get<InteriorTypeProductMetricResponse>(
        this.baseUrl + "PaymentTypeMetrics",
        {
          headers: this.httpHeaders.getHeadersPrivate(),
          params: { year },
        },
      )
      .pipe(
        tap((res) => {
          this._paymentTypeMetric.next(res.data);
        }),
        catchError((err) => {
          return throwError(() => err);
        }),
      );
  }

  interiorTypeProductMetric(
    year: number,
  ): Observable<InteriorTypeProductMetricResponse> {
    return this.httpClient
      .get<InteriorTypeProductMetricResponse>(
        this.baseUrl + "InteriorTypeMetrics",
        {
          headers: this.httpHeaders.getHeadersPrivate(),
          params: { year },
        },
      )
      .pipe(
        tap((res) => {
          this._interiorTypeProductMetric.next(res.data);
        }),
        catchError((err) => {
          return throwError(() => err);
        }),
      );
  }

  OpportunitiesDeparmentMetric(
    id: number,
  ): Observable<OpportunityDeparmentMetricResponse> {
    return this.httpClient
      .get<OpportunityDeparmentMetricResponse>(
        this.baseUrl + "DepartmentOpportunityMetrics",
        {
          headers: this.httpHeaders.getHeadersPrivate(),
          params: { ProjectId: id },
        },
      )
      .pipe(
        tap((res) => {
          this._opportunityDeparmentMetric.next(
            res.data.filter(({ total }) => total > 0),
          );
        }),
        catchError((err) => {
          return throwError(() => err);
        }),
      );
  }

  OpportunitiesWinLossResumeMetric(
    id: number,
  ): Observable<WinLossReasonsMetricResponse> {
    return this.httpClient
      .get<WinLossReasonsMetricResponse>(this.baseUrl + "ReasonWinAndLoss", {
        headers: this.httpHeaders.getHeadersPrivate(),
        params: { ProjectId: id },
      })
      .pipe(
        tap((res) => {
          this._opportunityWinLossReasonResumeMetric.next(res.data);
        }),
        catchError((err) => {
          return throwError(() => err);
        }),
      );
  }

  OpportunitiesResumeMetric(
    id: number,
  ): Observable<OpportunityResumeMetricResponse> {
    return this.httpClient
      .get<OpportunityResumeMetricResponse>(this.baseUrl + "WinAndLoss", {
        headers: this.httpHeaders.getHeadersPrivate(),
        params: { ProjectId: id },
      })
      .pipe(
        tap((res) => {
          this._opportunityResumeMetric.next(res.data);
        }),
        catchError((err) => {
          return throwError(() => err);
        }),
      );
  }

  OpportunitiesByAdvisor(id: number): Observable<OpportunityAdvisorResponse> {
    return this.httpClient
      .get<OpportunityAdvisorResponse>(
        this.baseUrl + "OpportunitiesByAdvisor",
        {
          headers: this.httpHeaders.getHeadersPrivate(),
          params: { ProjectId: id },
        },
      )
      .pipe(
        tap((res) => {
          this._opportunityAdvisor.next(res.data);
        }),
        catchError((err) => {
          return throwError(() => err);
        }),
      );
  }

  OpportunitiesKPI(
    params: {
      projectId?: number | null;
      initDate?: string | null;
      endDate?: string | null;
    } = {},
  ): Observable<ApiResponseData<OpportunityKpiMetric>> {
    const {
      projectId = null,
      initDate = null,
      endDate = null,
      ...rest
    } = params;

    const clean = cleanParams({ projectId, initDate, endDate, ...rest });

    const url = `${this.baseUrl}OpportunitiesKPI`;

    return this.httpClient
      .get<ApiResponseData<OpportunityKpiMetric>>(url, {
        headers: this.httpHeaders.getHeadersPrivate(),
        params: clean,
      })
      .pipe(
        tap((resp) => this._opportunitiesKpiMetrics.next(resp)),
        catchError((err) => throwError(() => err)),
      );
  }

  TotalComparativeKpi(
    params: {
      projectId?: number | null;
      initDate?: string | null;
      endDate?: string | null;
    } = {},
  ): Observable<ApiResponseData<ITotalComparativeKpi>> {
    const {
      projectId = null,
      initDate = null,
      endDate = null,
      ...rest
    } = params;

    const clean = cleanParams({ projectId, initDate, endDate, ...rest });

    const url = `${this.baseUrl}TotalComparativeKPI`;

    return this.httpClient
      .get<ApiResponseData<ITotalComparativeKpi>>(url, {
        headers: this.httpHeaders.getHeadersPrivate(),
        params: clean,
      })
      .pipe(
        tap((resp) => {
          this.storeData(resp, params);
          this._totalComparativeKpi.next(resp);
        }),
   catchError((err) => {
        this._totalComparativeKpi.next({
          succeeded: false,
          message: err?.error?.Message ?? err?.error?.message ?? 'Error al obtener los datos.',
          errors: null,
          data: null
        });
        return throwError(() => err);
      }),      );
  }

  private storeData(
    data: ApiResponseData<ITotalComparativeKpi>,
    params: {
      projectId?: number | null;
      initDate?: string | null;
      endDate?: string | null;
    },
  ): void {
    const storedObject = {
      data,
      expiry: new Date().getTime() + this.cacheDuration,
    };
    const paramsToStore: any = {};
    if (params.projectId) paramsToStore.projectId = params.projectId;

    localStorage.setItem(
      `${this.storageKey}${params?.projectId?.toString()}`,
      JSON.stringify(storedObject),
    );
  }

  private getStoredData(
    params: OpportunitiesKpiParams,
  ): ApiResponseData<ITotalComparativeKpi> | null {
    const paramsCopy: any = {};
    paramsCopy.projectId = params.projectId;

    const storedObjectStr = localStorage.getItem(
      `${this.storageKey}${paramsCopy?.projectId?.toString()}`,
    );
    const storedObject = JSON.parse(storedObjectStr);

    if (!storedObjectStr) {
      return null;
    }

    const now = new Date().getTime();

    if (now > storedObject.expiry) {
      localStorage.removeItem(this.storageKey);
      return null;
    }

    return storedObject?.data as ApiResponseData<ITotalComparativeKpi>;
  }

  ProductStatusKPI(
    params: {
      projectId?: number | null;
      initDate?: string | null;
      endDate?: string | null;
    } = {},
  ): Observable<ApiResponseData<ProducStatusData>> {
    const {
      projectId = null,
      initDate = null,
      endDate = null,
      ...rest
    } = params;

    const clean = cleanParams({ projectId, initDate, endDate, ...rest });

    const url = `${this.baseUrl}ProductStatusKPI`;

    return this.httpClient
      .get<ApiResponseData<ProducStatusData>>(url, {
        headers: this.httpHeaders.getHeadersPrivate(),
        params: clean,
      })
      .pipe(
        tap((resp) => this._productStatusKpi.next(resp)),
        catchError((err) => throwError(() => err)),
      );
  }

  LeadsKpi(
    params: {
      projectId?: number | null;
      initDate?: string | null;
      endDate?: string | null;
    } = {},
  ): Observable<ApiResponseData<LeadsKpiData>> {
    const {
      projectId = null,
      initDate = null,
      endDate = null,
      ...rest
    } = params;

    const clean = cleanParams({ projectId, initDate, endDate, ...rest });

    const url = `${this.baseUrl}LeadsKPI`;

    return this.httpClient
      .get<ApiResponseData<LeadsKpiData>>(url, {
        headers: this.httpHeaders.getHeadersPrivate(),
        params: clean,
      })
      .pipe(tap((resp) => this._leadsKpi.next(resp)));
  }

  TopOpportunitiesMetric(projectId: number): Observable<OpportunitiesResponse> {
    return this.httpClient
      .get<OpportunitiesResponse>(
        this.baseUrl + "TopActiveOpportunitiesMetric",
        {
          headers: this.httpHeaders.getHeadersPrivate(),
          params: { ProjectId: projectId },
        },
      )
      .pipe(
        tap((res) => {
          this._topOpportunitiesMetric.next(res.data);
        }),
        catchError((err) => {
          return throwError(() => err);
        }),
      );
  }

  OpportunityStepMetrics(
    start: string,
    end: string,
    projectId: number,
  ): Observable<OpportunityStepMetricResponse> {
    return this.httpClient
      .get<OpportunityStepMetricResponse>(
        this.baseUrl + "OpportunityStepMetrics",
        {
          headers: this.httpHeaders.getHeadersPrivate(),
          params: { startDate: start, endDate: end, ProjectId: projectId },
        },
      )
      .pipe(
        tap((res) => {
          this._opportunityStepMetric.next(res.data);
        }),
        catchError((err) => {
          return throwError(() => err);
        }),
      );
  }

  OpportunityFunnelStepMetrics(
    start: string,
    end: string,
    projectId: number,
  ): Observable<OpportunityStepMetricResponse> {
    return this.httpClient
      .get<OpportunityStepMetricResponse>(
        this.baseUrl + "OpportunityFunnelStepMetrics",
        {
          headers: this.httpHeaders.getHeadersPrivate(),
          params: { startDate: start, endDate: end, ProjectId: projectId },
        },
      )
      .pipe(
        tap((res) => {
          this._opportunityFunnelStepMetric.next(res.data);
        }),
        catchError((err) => {
          return throwError(() => err);
        }),
      );
  }

  OpportunityLossReasonMetrics(
    start: string,
    end: string,
    projectId: number,
  ): Observable<OpportunityReasonMetricsResponse> {
    return this.httpClient
      .get<OpportunityReasonMetricsResponse>(
        this.baseUrl + "OpportunityLossReasonMetrics",
        {
          headers: this.httpHeaders.getHeadersPrivate(),
          params: { startDate: start, endDate: end, ProjectId: projectId },
        },
      )
      .pipe(
        tap((res) => {
          this._opportunityLossReasonMetric.next(res.data);
        }),
        catchError((err) => {
          return throwError(() => err);
        }),
      );
  }

  OpportunityWinReasonMetrics(
    start: string,
    end: string,
    projectId: number,
  ): Observable<OpportunityReasonMetricsResponse> {
    return this.httpClient
      .get<OpportunityReasonMetricsResponse>(
        this.baseUrl + "OpportunityWinReasonMetrics",
        {
          headers: this.httpHeaders.getHeadersPrivate(),
          params: { startDate: start, endDate: end, ProjectId: projectId },
        },
      )
      .pipe(
        tap((res) => {
          this._opportunityWinReasonMetric.next(res.data);
        }),
        catchError((err) => {
          return throwError(() => err);
        }),
      );
  }

  LostOpportunityMetrics(
    year: number,
    projectId: number,
  ): Observable<LostOpportunityMetricResponse> {
    return this.httpClient
      .get<LostOpportunityMetricResponse>(
        this.baseUrl + "LostOpportunitiesByYear",
        {
          headers: this.httpHeaders.getHeadersPrivate(),
          params: { Year: year, ProjectId: projectId },
        },
      )
      .pipe(
        tap((res) => {
          this._lostOpportunityMetric.next(res.data);
        }),
        catchError((err) => {
          return throwError(() => err);
        }),
      );
  }

  OpportunityOriginMetrics(
    projectId: number,
  ): Observable<OpportunityReasonMetricsResponse> {
    return this.httpClient
      .get<OpportunityReasonMetricsResponse>(
        this.baseUrl + "OpportunityOriginMetrics",
        {
          headers: this.httpHeaders.getHeadersPrivate(),
          params: { ProjectId: projectId },
        },
      )
      .pipe(
        tap((res) => {
          this._opportunityOriginMetric.next(res.data);
        }),
        catchError((err) => {
          return throwError(() => err);
        }),
      );
  }

  NumSalesByAdvisor(
    year: number,
    projectId: number,
  ): Observable<NumSalesAdvisorResponse> {
    return this.httpClient
      .get<NumSalesAdvisorResponse>(this.baseUrl + "NumSalesFromYear", {
        headers: this.httpHeaders.getHeadersPrivate(),
        params: { Year: year, ProjectId: projectId },
      })
      .pipe(
        tap((res) => {
          this._numSalesByAdvisor.next(res.data);
        }),
        catchError((err) => {
          return throwError(() => err);
        }),
      );
  }

  SalesByAdvisor(
    year: number,
    projectId: number,
  ): Observable<SalesByAdvisorResponse> {
    return this.httpClient
      .get<SalesByAdvisorResponse>(this.baseUrl + "SalesFromYear", {
        headers: this.httpHeaders.getHeadersPrivate(),
        params: { Year: year, ProjectId: projectId },
      })
      .pipe(
        tap((res) => {
          this._salesByAdvisor.next(res.data);
        }),
        catchError((err) => {
          return throwError(() => err);
        }),
      );
  }

  AdvisorPendingActivities(
    projectId: number,
  ): Observable<PendingActivitiesResponse> {
    return this.httpClient
      .get<PendingActivitiesResponse>(
        this.baseUrl + "AdvisorPendingActivities",
        {
          headers: this.httpHeaders.getHeadersPrivate(),
          params: { projectId: projectId },
        },
      )
      .pipe(
        tap((res) => {
          this._pendingActivitiesMetric.next(res.data);
        }),
        catchError((err) => {
          return throwError(() => err);
        }),
      );
  }

  ProductYearSalesMetric(
    year: number,
    projectId: number,
  ): Observable<ProductYearSalesMetricResponse> {
    return this.httpClient
      .get<ProductYearSalesMetricResponse>(this.baseUrl + "ProductYearSales", {
        headers: this.httpHeaders.getHeadersPrivate(),
        params: { Year: year, ProjectId: projectId },
      })
      .pipe(
        tap((res) => {
          this._productYearSalesMetric.next(res.data);
        }),
        catchError((err) => {
          return throwError(() => err);
        }),
      );
  }

  CategoryTotalSalesMetric(
    startDate: string,
    endDate: string,
    projectId: number,
    step: number,
  ): Observable<CategorySalesMetricResponse> {
    return this.httpClient
      .get<CategorySalesMetricResponse>(
        this.baseUrl + "CategoryTotalSalesMetrics",
        {
          headers: this.httpHeaders.getHeadersPrivate(),
          params: {
            StartDate: startDate,
            EndDate: endDate,
            ProjectId: projectId,
            OpportunityStepId: step,
          },
        },
      )
      .pipe(
        tap((res) => {
          this._categorySalesMetric.next(res.data);
        }),
        catchError((err) => {
          return throwError(() => err);
        }),
      );
  }

  CategoryNumProductSold(
    startDate: string,
    endDate: string,
    projectId: number,
    step: number,
  ): Observable<CategorySalesMetricResponse> {
    return this.httpClient
      .get<CategorySalesMetricResponse>(
        this.baseUrl + "CategoryNumProductSold",
        {
          headers: this.httpHeaders.getHeadersPrivate(),
          params: {
            StartDate: startDate,
            EndDate: endDate,
            ProjectId: projectId,
            OpportunityStepId: step,
          },
        },
      )
      .pipe(
        tap((res) => {
          this._categoryNumProductSoldMetric.next(res.data);
        }),
        catchError((err) => {
          return throwError(() => err);
        }),
      );
  }

  GlobalGoalMetric(ProjectId: number): Observable<GoalMetricsResponse> {
    return this.httpClient
      .get<GoalMetricsResponse>(this.baseUrl + "GlobalGoalMetric", {
        headers: this.httpHeaders.getHeadersPrivate(),
        params: { ProjectId },
      })
      .pipe(
        tap((resp) => {
          this._goalMetricProject.next(resp);
        }),
        catchError((err) => {
          return throwError(() => err);
        }),
      );
  }

  SalesPerMonthMetric(ProjectId: number): Observable<
    ApiResponseData<{
      salesPerMonth: SalesPerMonthMetric[];
      salesPerWeek: SalesPerMonthWeekMetric[];
    }>
  > {
    return this.httpClient
      .get<
        ApiResponseData<{
          salesPerMonth: SalesPerMonthMetric[];
          salesPerWeek: SalesPerMonthWeekMetric[];
        }>
      >(this.baseUrl + "SalesPerMonth", {
        headers: this.httpHeaders.getHeadersPrivate(),
        params: { ProjectId },
      })
      .pipe(
        tap((resp) => {
          this._salesPerMonthMetric.next(resp);
        }),
        catchError((err) => {
          return throwError(() => err);
        }),
      );
  }

  ClientPerProject(
    payload: {
      projectId?: number | null;
      initDate?: string | null;
      endDate?: string | null;
    } = { projectId: null, initDate: null, endDate: null },
  ): Observable<ApiResponseData<ClientsPerProject[]>> {
    const { projectId = null, initDate = null, endDate = null } = payload;

    const params = cleanParams({ projectId, initDate, endDate });

    return this.httpClient
      .get<ApiResponseData<ClientsPerProject[]>>(
        this.baseUrl + "ClientsPerProject",
        {
          headers: this.httpHeaders.getHeadersPrivate(),
          params,
        },
      )
      .pipe(
        tap((resp) => this._clientsPerProject.next(resp)),
        catchError((err) => throwError(() => err)),
      );
  }

  TotalCommissionKPI(
    payload: {
      projectId?: number | null;
      initDate?: string | null;
      endDate?: string | null;
    } = { projectId: null, initDate: null, endDate: null },
  ): Observable<ApiResponseData<ICommissionCenter>> {
    const { projectId = null, initDate = null, endDate = null } = payload;

    const params = cleanParams({ ProjectId: projectId, initDate, endDate });

    return this.httpClient
      .get<ApiResponseData<ICommissionCenter>>(
        this.baseUrl + "TotalCommissionKPI",
        {
          headers: this.httpHeaders.getHeadersPrivate(),
          params,
        },
      )
      .pipe(
       tap((resp) => {
      this._commissionCenter.next(resp);
    }),
    catchError((err) => {
      return throwError(() => err);
    }),
  );
  }

  LeadFunnelKPI(params: {
    projectId?: number | null;
    userId?: string | null;
    year?: number | null;
    month?: number | null;
    from?: string | null;
    to?: string | null;
    source?: LeadFunnelSource | null;
    view?: LeadFunnelView | null;
    company?: string | null;
  }): Observable<ApiResponseData<LeadFunnelKpi>> {
    return this.httpClient
      .get<ApiResponseData<LeadFunnelKpi>>(this.baseUrl + "LeadFunnelKPI", {
        headers: this.httpHeaders.getHeadersPrivate(),
        params: cleanParams(params),
      })
      .pipe(catchError((err) => throwError(() => err)));
  }

  DocumentationComplianceKpi(params: {
    projectId?: number | null;
  }): Observable<ApiResponseData<DocumentationComplianceKpi>> {
    return this.httpClient
      .get<ApiResponseData<DocumentationComplianceKpi>>(this.baseUrl + "DocumentationComplianceKpi", {
        headers: this.httpHeaders.getHeadersPrivate(),
        params: cleanParams(params),
      })
      .pipe(catchError((err) => throwError(() => err)));
  }

  ReservationContractKpi(params: {
    projectId?: number | null;
    year?: number | null;
    month?: number | null;
  }): Observable<ApiResponseData<ReservationContractKpi>> {
    return this.httpClient
      .get<ApiResponseData<ReservationContractKpi>>(this.baseUrl + "ReservationContractKpi", {
        headers: this.httpHeaders.getHeadersPrivate(),
        params: cleanParams(params),
      })
      .pipe(catchError((err) => throwError(() => err)));
  }

  ReservationContractReport(params: {
    projectId?: number | null;
    year?: number | null;
    month?: number | null;
  }): Observable<ApiResponseData<ReservationContractReport>> {
    return this.httpClient
      .get<ApiResponseData<ReservationContractReport>>(this.baseUrl + "ReservationContractReport", {
        headers: this.httpHeaders.getHeadersPrivate(),
        params: cleanParams(params),
      })
      .pipe(catchError((err) => throwError(() => err)));
  }

  PaymentSapReconciliationKpi(params: {
    projectId?: number | null;
    year: number;
    month: number;
    verifySap?: boolean;
  }): Observable<ApiResponseData<PaymentSapReconciliationKpi>> {
    return this.httpClient
      .get<ApiResponseData<PaymentSapReconciliationKpi>>(this.baseUrl + "PaymentSapReconciliationKpi", {
        headers: this.httpHeaders.getHeadersPrivate(),
        params: cleanParams(params),
      })
      .pipe(catchError((err) => throwError(() => err)));
  }

  InternalExternalSalesKpi(params: {
    projectId?: number | null;
    from?: string | null;
    to?: string | null;
    source?: LeadFunnelSource | null;
    company?: string | null;
    userId?: string | null;
    categoryId?: number | null;
  }): Observable<ApiResponseData<InternalExternalSalesKpi>> {
    return this.httpClient
      .get<ApiResponseData<InternalExternalSalesKpi>>(this.baseUrl + "InternalExternalSalesKpi", {
        headers: this.httpHeaders.getHeadersPrivate(),
        params: cleanParams(params),
      })
      .pipe(catchError((err) => throwError(() => err)));
  }

  AppreciationKpi(projectId: number, company: string | null = null, categoryId: number | null = null): Observable<ApiResponseData<AppreciationKpi>> {
    return this.httpClient
      .get<ApiResponseData<AppreciationKpi>>(this.baseUrl + "AppreciationKpi", {
        headers: this.httpHeaders.getHeadersPrivate(),
        params: cleanParams({ projectId, company, categoryId }),
      })
      .pipe(catchError((err) => throwError(() => err)));
  }

  PenaltyIncomeKpi(params: {
    projectId?: number | null;
    from?: string | null;
    to?: string | null;
    company?: string | null;
    userId?: string | null;
    categoryId?: number | null;
  }): Observable<ApiResponseData<PenaltyIncomeKpi>> {
    return this.httpClient
      .get<ApiResponseData<PenaltyIncomeKpi>>(this.baseUrl + "PenaltyIncomeKpi", {
        headers: this.httpHeaders.getHeadersPrivate(),
        params: cleanParams(params),
      })
      .pipe(catchError((err) => throwError(() => err)));
  }

  ProductsOverdueAmountKpi(params: {
    projectId?: number | null;
    initDate?: string | null;
    endDate?: string | null;
  }): Observable<ApiResponseData<ProductsOverdueAmountKpi[]>> {
    return this.httpClient
      .get<ApiResponseData<ProductsOverdueAmountKpi[]>>(this.baseUrl + "ProductsOverdueAmountKpi", {
        headers: this.httpHeaders.getHeadersPrivate(),
        params: cleanParams(params),
      })
      .pipe(catchError((err) => throwError(() => err)));
  }

  LossReasonKpi(params: {
    projectId?: number | null;
    from?: string | null;
    to?: string | null;
    company?: string | null;
    fromReservation?: boolean | null;
    userId?: string | null;
  }): Observable<ApiResponseData<LossReasonKpi>> {
    return this.httpClient
      .get<ApiResponseData<LossReasonKpi>>(this.baseUrl + "LossReasonKpi", {
        headers: this.httpHeaders.getHeadersPrivate(),
        params: cleanParams(params),
      })
      .pipe(catchError((err) => throwError(() => err)));
  }

  LeadAdvisorKpi(params: {
    projectId?: number | null;
    userId?: string | null;
    year?: number | null;
    month?: number | null;
    from?: string | null;
    to?: string | null;
    source?: LeadFunnelSource | null;
    company?: string | null;
  }): Observable<ApiResponseData<LeadAdvisorKpi>> {
    return this.httpClient
      .get<ApiResponseData<LeadAdvisorKpi>>(this.baseUrl + "LeadAdvisorKpi", {
        headers: this.httpHeaders.getHeadersPrivate(),
        params: cleanParams(params),
      })
      .pipe(catchError((err) => throwError(() => err)));
  }

  InterCompanies(): Observable<ApiResponseData<InterCompany[]>> {
    return this.httpClient
      .get<ApiResponseData<InterCompany[]>>(this.baseUrl + "InterCompanies", {
        headers: this.httpHeaders.getHeadersPrivate(),
      })
      .pipe(catchError((err) => throwError(() => err)));
  }

  InterCompanyProjects(company: string | null): Observable<ApiResponseData<InterCompanyProject[]>> {
    return this.httpClient
      .get<ApiResponseData<InterCompanyProject[]>>(this.baseUrl + "InterCompanyProjects", {
        headers: this.httpHeaders.getHeadersPrivate(),
        params: cleanParams({ All: true, company }),
      })
      .pipe(catchError((err) => throwError(() => err)));
  }

  InterCompanyCategories(projectId: number, company: string | null): Observable<CategoriesResponse> {
    return this.httpClient
      .get<CategoriesResponse>(this.baseUrl + "InterCompanyCategories", {
        headers: this.httpHeaders.getHeadersPrivate(),
        params: cleanParams({ ProjectId: projectId, All: true, PageNumber: 1, PageSize: 200, company }),
      })
      .pipe(catchError((err) => throwError(() => err)));
  }

  ReportScopes(): Observable<ReportScope[]> {
    return this.InterCompanies().pipe(
      switchMap((response) => {
        const companies = response?.data ?? [];
        if (companies.length < 2) return of([] as { company: InterCompany; projects: InterCompanyProject[] }[]);
        return forkJoin(
          companies.map((company) =>
            this.InterCompanyProjects(company.key).pipe(
              map((projects) => ({ company, projects: projects?.data ?? [] })),
            ),
          ),
        );
      }),
      map((groups) => {
        if (!groups.length) return [] as ReportScope[];
        const scopes: Omit<ReportScope, "id">[] = [
          { name: "Todos los proyectos", company: ALL_COMPANIES_KEY, projectId: null },
        ];
        for (const { company, projects } of groups) {
          const key = company.isLocal ? null : company.key;
          if (projects.length > 1) scopes.push({ name: `Todo ${company.name}`, company: key, projectId: null });
          for (const project of projects)
            scopes.push({ name: `${company.name} · ${project.name}`, company: key, projectId: project.id });
        }
        return scopes.map((scope, index) => ({ ...scope, id: index + 1 }));
      }),
    );
  }

  ProspectFunnelKPI(
    payload: {
      projectId?: number | null;
      userId?: string | null;
      initDate?: string | null;
      endDate?: string | null;
    } = { projectId: null, userId: null, initDate: null, endDate: null },
  ): Observable<ApiResponseData<ProspectFunnel[]>> {
    const { projectId = null, userId = null, initDate = null, endDate = null } = payload;

    const params = cleanParams({ ProjectId: projectId, UserId: userId, initDate, endDate });

    return this.httpClient
      .get<ApiResponseData<ProspectFunnel[]>>(
        this.baseUrl + "ProspectFunnelKPI",
        {
          headers: this.httpHeaders.getHeadersPrivate(),
          params,
        },
      )
      .pipe(
        tap((resp) => this._prospectFunnelKpi.next(resp)),
        catchError((err) => throwError(() => err)),
      );
  }
}
